import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { Users, Eye } from 'lucide-react';

function getOrCreateAnonId() {
  let id = localStorage.getItem('pp_anon_id');
  if (!id) { id = crypto.randomUUID(); localStorage.setItem('pp_anon_id', id); }
  return id;
}

export default function JoinPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // ?observer=true pre-selects observer mode (spectator link)
  const defaultObserver = searchParams.get('observer') === 'true';

  const [session, setSession] = useState(null);
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [isObserver, setIsObserver] = useState(defaultObserver);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // If already joined this session, go straight in
    const pid = localStorage.getItem(`pp_participant_${sessionId}`);
    if (pid) { navigate(`/session/${sessionId}`, { replace: true }); return; }

    api.sessions.get(sessionId)
      .then(data => {
        setSession(data);
        // If logged-in user is already a participant, go straight in
        if (user) {
          const myP = (data.participants || []).find(p => p.jira_id === user.jiraId && p.is_active);
          if (myP) {
            localStorage.setItem(`pp_participant_${sessionId}`, myP.id);
            navigate(`/session/${sessionId}`, { replace: true });
          }
        }
      })
      .catch(() => setError('Session not found or no longer active.'))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const handleJoin = async () => {
    if (!displayName.trim()) return;
    setJoining(true);
    try {
      const { participant } = await api.sessions.join(sessionId, {
        display_name: displayName.trim(),
        anonymous_id: getOrCreateAnonId(),
        is_observer: isObserver
      });
      localStorage.setItem(`pp_participant_${sessionId}`, participant.id);
      navigate(`/session/${sessionId}`);
    } catch (e) {
      setError(e.message);
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="page flex items-center justify-center" style={{ minHeight: '60vh' }}>
        <p className="text-muted">Loading session...</p>
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="page flex items-center justify-center" style={{ minHeight: '60vh' }}>
        <div className="card" style={{ maxWidth: 400, textAlign: 'center', padding: '48px 32px' }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>😕</div>
          <h2 style={{ marginBottom: 8 }}>Session Not Found</h2>
          <p className="text-muted mb-6">{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>Go Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page flex items-center justify-center" style={{ minHeight: '60vh' }}>
      <div className="card" style={{ maxWidth: 440, width: '100%', padding: '40px 32px' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>🃏</div>
          <h2 style={{ marginBottom: 4 }}>
            {defaultObserver ? 'Join as Observer' : 'Join Session'}
          </h2>
          {session && (
            <>
              <p className="font-bold" style={{ marginBottom: 4 }}>{session.name}</p>
              <p className="text-sm text-muted">
                {session.teams?.name} · Hosted by {session.facilitator_name}
              </p>
              <div className="flex items-center justify-center gap-3 mt-3">
                <span className="badge badge-green">Active</span>
                {session.participants?.length > 0 && (
                  <span className="badge badge-muted">
                    <Users size={10} /> {session.participants.length} joined
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        <div className="input-group">
          <label className="input-label">Your Name</label>
          <input
            className="input"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleJoin()}
            placeholder="Enter your display name..."
            autoFocus
          />
        </div>

        {/* Observer toggle — pre-checked if coming from observer link */}
        <div
          className="flex items-center gap-3"
          style={{
            padding: '12px 16px',
            background: isObserver ? 'var(--blue-dim)' : 'var(--bg-elevated)',
            border: `1px solid ${isObserver ? 'var(--blue)' : 'var(--border)'}`,
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            marginBottom: 20,
            transition: 'all 0.15s'
          }}
          onClick={() => setIsObserver(o => !o)}
        >
          <Eye size={16} style={{ color: isObserver ? 'var(--blue)' : 'var(--text-muted)' }} />
          <div style={{ flex: 1 }}>
            <div className="text-sm font-bold" style={{ color: isObserver ? 'var(--blue)' : 'var(--text-primary)' }}>
              Join as Observer
            </div>
            <div className="text-xs text-muted">Watch without voting</div>
          </div>
          <div style={{
            width: 20, height: 20, borderRadius: '50%',
            background: isObserver ? 'var(--blue)' : 'var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, color: 'white', transition: 'all 0.15s'
          }}>
            {isObserver ? '✓' : ''}
          </div>
        </div>

        {error && <div className="toast toast-error mb-4">{error}</div>}

        <button className="btn btn-primary w-full btn-lg" onClick={handleJoin}
          disabled={joining || !displayName.trim()}>
          {joining ? 'Joining...' : isObserver ? '👁 Join as Observer' : '🃏 Join and Vote'}
        </button>

        <p className="text-xs text-muted mt-4" style={{ textAlign: 'center' }}>
          No account needed.
        </p>
      </div>
    </div>
  );
}
